import axios from "axios";
import { get_headers, AML_VERIFICATION_URL, AML_VERIFIED_DATA } from "@/utils/constants/constants.js";
import {
  extractAMLCheckResults,
  calculateAMLRiskScore
} from "@/services/verification/integrated_verification_apis/aml_data_extractor";

/**
 * Detects if current context is lead or application
 * @returns {Object} Context type and ID
 */
function getVerificationContext() {
  const application_form_id = sessionStorage.getItem("application_form_id");
  const lead_form_id = sessionStorage.getItem("lead_form_id");
  const lead_id = sessionStorage.getItem("lead_id");

  if (lead_form_id || lead_id) {
    return { 
      type: 'lead', 
      lead_form_id: lead_form_id || null,
      lead_id: lead_id || null 
    };
  }
  
  return { 
    type: 'application', 
    application_form_id: application_form_id || null 
  };
}

/**
 * AML Verification API function
 * @param {Object} payload - Contains service_id, form_id, context
 * @returns {Promise} - API response
 */
export const amlVerify = async (payload) => {
  const headers = get_headers();
  
  const context = getVerificationContext();
  const enrichedPayload = {
    ...payload,
    ...context
  };

  const response = await axios.post(AML_VERIFICATION_URL, enrichedPayload, { headers });
  return typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
};

/**
 * Build query parameters for AML verification
 * @param {Object} params - Input parameters
 * @returns {Object} Query parameters or error object
 */
function buildQueryParams(params) {
  if (!params.service_id) {
    return {
      error: true,
      response: {
        status: "error",
        message: "Service ID is required",
        results: [],
        service_name: "AML Verification",
      }
    };
  }

  const query_params = { service_id: params.service_id };

  if (params.entity_profile_id) {
    query_params.entity_profile_id = params.entity_profile_id;
  } else if (params.lead_id) {
    query_params.lead_id = params.lead_id;
  } else {
    const entity_id = sessionStorage.getItem("entity_profile_id");
    const lead_id = sessionStorage.getItem("lead_id");
    
    if (entity_id) {
      query_params.entity_profile_id = entity_id;
    } else if (lead_id) {
      query_params.lead_id = lead_id;
    } else {
      return {
        error: true,
        response: {
          status: "error",
          message: "Either entity_profile_id or lead_id is required",
          results: [],
          service_name: "AML Verification",
        }
      };
    }
  }

  return { error: false, query_params };
}

/**
 * Normalize verification status
 * @param {string} status - Raw status from API
 * @returns {string} Normalized status ('success' or 'fail')
 */
function normalizeStatus(status) {
  const status_text = (status || "").toLowerCase();
  return status_text.includes("success") ||
         status_text.includes("pass") ||
         status_text.includes("verified") ||
         status_text.includes("completed")
    ? "success"
    : "fail";
}

/**
 * Format AML data structure from API response
 * @param {Object} results - Raw results data from API
 * @returns {Object} Formatted results data
 */
function formatAMLData(results) {
  const home_affairs = results.HomeAffairs?.RealTimeIDV || {};
  const supplier_data_array = results.HomeAffairs?.SupplierData || [];

  return {
    "Home Affairs": home_affairs,
    "Supplier Data": supplier_data_array,
  };
}

/**
 * Format successful verification response with AML data extraction
 * @param {Object} data - API response data
 * @returns {Object} Formatted response
 */
function formatSuccessResponse(data) {
  const normalized_status = normalizeStatus(data.status);
  const default_message = normalized_status === "fail" ? "System verification failed." : "";

  // Extract and format results data
  const results = data.results_data || data.data || data.details || {};
  const formatted_results_data = formatAMLData(results);

  // Extract UI-friendly data
  const aml_check_results = extractAMLCheckResults(formatted_results_data);
  const risk_score = calculateAMLRiskScore(aml_check_results);

  const result_object = {
    status: normalized_status,
    message: data.message || default_message,
    field_results: data.field_results || {},
    results_data: formatted_results_data,
    
    // Add extracted data for easy UI consumption
    aml_check_results,
    risk_score,
    
    service_name: "AML Verification",
  };

  return {
    status: normalized_status,
    results: [result_object],
    raw_data: data,
    service_name: "AML Verification",
  };
}

/**
 * Format error response
 * @param {Object} data - Error data from API
 * @returns {Object} Formatted error response
 */
function formatErrorResponse(data) {
  return {
    status: "error",
    message: data.message,
    results: [{
      status: "error",
      message: data.message,
      field_results: {},
      results_data: {},
      service_name: "AML Verification",
    }],
    raw_data: data,
    service_name: "AML Verification",
  };
}

/**
 * Get AML Verification Results — ENHANCED WITH EXTRACTED DATA
 * @param {Object} params - Contains service_id AND either entity_profile_id OR lead_id
 * @returns {Promise} - API response with verification results
 */
export const getAMLVerificationResults = async (params = {}) => {
  const headers = get_headers();

  try {
    const queryResult = buildQueryParams(params);
    if (queryResult.error) {
      return queryResult.response;
    }

    const response = await axios.get(AML_VERIFIED_DATA, {
      headers,
      params: queryResult.query_params,
    });

    const data = typeof response.data === "string" ? JSON.parse(response.data) : response.data;

    if (data.status === "error") {
      return formatErrorResponse(data);
    }

    return formatSuccessResponse(data);
  } catch (error) {
    return {
      status: "error",
      message:
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch verification results",
      results: [],
      raw_data: null,
      service_name: "AML Verification",
    };
  }
};